import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import TwoSidedFlow from "@/components/TwoSidedFlow";
import FeaturedArtists from "@/components/FeaturedArtists";
import ReviewMediationAnimation from "@/components/ReviewMediationAnimation";
import { TAXONOMY } from "@/data/taxonomy";
import { CATEGORY_IMAGES, FALLBACK_CATEGORY_IMAGE } from "@/data/categoryImages";
import {
  ArrowRight,
  ArrowLeftRight,
  ShieldCheck,
  Lock,
  Building2,
  Users,
  TrendingUp,
  Star,
  Quote,
  Check,
  Megaphone,
  Mail,
  Gift,
  BarChart3,
  Target,
  Sparkles,
} from "lucide-react";

const Pill = ({ children, tone = "muted", testId }) => {
  const tones = {
    muted: "bg-[#F5EEEA] text-[#3E2A26] border border-[#EADAD3]",
    red: "bg-[#F9E4E1] text-[#B31E1E] border border-[#F0C9C4]",
    popular: "bg-[#B31E1E] text-white",
  };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium tracking-wide ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

const FeatureCard = ({ Icon, badge, title, body, image, testId }) => (
  <div
    className="group relative rounded-2xl border border-[#EAE3DE] bg-white overflow-hidden hover:border-[#E5B7B1] hover:shadow-[0_4px_24px_-8px_rgba(179,30,30,0.15)] transition-all"
    data-testid={testId}
  >
    {image && (
      <div className="relative h-40 md:h-44 overflow-hidden bg-[#F5EEEA]">
        <img
          src={image}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {/* subtle gradient wash so the badge stays legible on any photo */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-3 right-3">
          <Pill tone="red">{badge}</Pill>
        </div>
      </div>
    )}
    <div className="p-6 md:p-7">
      {!image && (
        <div className="flex items-start justify-between mb-5">
          <div className="w-11 h-11 rounded-xl bg-[#F9E4E1] flex items-center justify-center">
            <Icon className="text-[#B31E1E]" size={20} />
          </div>
          <Pill tone="red">{badge}</Pill>
        </div>
      )}
      {image && (
        <div className="w-10 h-10 rounded-xl bg-[#F9E4E1] flex items-center justify-center -mt-11 mb-3 relative shadow-sm border border-white">
          <Icon className="text-[#B31E1E]" size={18} />
        </div>
      )}
      <h3 className="text-xl font-semibold text-[#1B1614] tracking-tight">{title}</h3>
      <p className="mt-2.5 text-[15px] leading-relaxed text-[#5C4E48]">{body}</p>
    </div>
  </div>
);

const Step = ({ n, title, body }) => (
  <div className="flex gap-4">
    <div className="shrink-0 w-11 h-11 rounded-lg border border-[#EAE3DE] bg-white flex items-center justify-center font-semibold text-[#1B1614]">
      {n}
    </div>
    <div className="pt-1">
      <h4 className="text-lg font-semibold text-[#1B1614] tracking-tight">{title}</h4>
      <p className="mt-1.5 text-[15px] leading-relaxed text-[#5C4E48]">{body}</p>
    </div>
  </div>
);

export default function Landing() {
  return (
    <div className="bg-[#FDFBF9] text-[#1B1614]">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#EAE3DE]">
        {/* decorative pale-red circle */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-20 w-[420px] h-[420px] rounded-full bg-[#F5D8D3] opacity-70 blur-[2px]"
        />
        {/* soft warm gradient */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#FCE9E5] via-[#FDF6F2] to-[#FDFBF9]"
        />

        <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-20 md:pb-28 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#F0C9C4] bg-white px-3.5 py-1.5 text-[11px] font-semibold tracking-wider uppercase text-[#1B1614] shadow-sm">
            <Star size={12} className="fill-[#B31E1E] text-[#B31E1E]" />
            Two-sided reputation
          </div>

          <h1 className="mt-7 text-4xl sm:text-6xl lg:text-[72px] leading-[1.02] font-black tracking-tight text-[#1B1614] max-w-4xl mx-auto">
            Where customers <span className="text-[#B31E1E]">&amp; businesses</span> hold each other accountable.
          </h1>

          <p className="mt-7 mx-auto max-w-2xl text-lg leading-relaxed text-[#5C4E48]">
            Servrevu breaks from one-directional ratings. Businesses rate customers alongside customer reviews — building genuine trust on both sides of every transaction.
          </p>

          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/list-your-business">
              <Button
                data-testid="hero-list-business"
                className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white h-12 px-6 text-[15px] font-semibold shadow-sm"
              >
                List your business <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <a href="#how-it-works">
              <Button
                variant="outline"
                data-testid="hero-how-it-works"
                className="rounded-lg h-12 px-6 text-[15px] font-semibold border-[#EAE3DE] bg-white hover:bg-[#FAF5F2]"
              >
                See how it works
              </Button>
            </a>
          </div>
          <p className="mt-4 text-sm text-[#8B7B73]">Free to list. No credit card required.</p>
        </div>

        {/* stat strip */}
        <div className="relative bg-white border-t border-[#EAE3DE]">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {(() => {
              const totalCategories = TAXONOMY.reduce((n, g) => n + (g.items?.length || 0), 0);
              // Round DOWN to nearest 10 so the number never overstates the taxonomy count.
              const roundedDown = Math.max(1, Math.floor(totalCategories / 10) * 10);
              return [
                { k: `${roundedDown}+`, v: "Service categories", testId: "stat-service-categories", link: "/categories" },
                { k: "2-way", v: "Rating system" },
                { k: "Privacy", v: "First-name-only profiles" },
                { k: "$0", v: "Free tier forever" },
              ];
            })().map((s) => {
              const testId = s.testId || `stat-${s.v.split(" ")[0].toLowerCase()}`;
              if (s.link) {
                return (
                  <Link
                    key={s.v}
                    to={s.link}
                    data-testid={testId}
                    className="group cursor-pointer transition hover:-translate-y-0.5"
                    aria-label={`See all ${s.v.toLowerCase()}`}
                  >
                    <div className="text-4xl md:text-5xl font-black tracking-tight text-[#1B1614] group-hover:text-[#B31E1E] transition-colors">{s.k}</div>
                    <div className="mt-1.5 text-sm text-[#8B7B73] group-hover:text-[#5C4E48] underline-offset-4 group-hover:underline transition-colors">{s.v}</div>
                  </Link>
                );
              }
              return (
                <div key={s.v} data-testid={testId}>
                  <div className="text-4xl md:text-5xl font-black tracking-tight text-[#1B1614]">{s.k}</div>
                  <div className="mt-1.5 text-sm text-[#8B7B73]">{s.v}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Two-sided animated flow */}
      <TwoSidedFlow />

      {/* Popular categories — visual entry-point to the directory. Curated
          list of the 12 most-searched top-level groups from TAXONOMY, each
          tile is a photo + label that links to the filtered /directory. */}
      <section className="bg-white border-b border-[#EAE3DE]" data-testid="popular-categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-[#F9E4E1] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#B31E1E]">
                Explore
              </div>
              <h2 className="mt-4 text-4xl md:text-5xl font-black tracking-tight text-[#1B1614]">
                Popular categories
              </h2>
              <p className="mt-3 text-[16px] text-[#5C4E48] max-w-xl">
                Tap any tile to browse verified, reviewed local businesses in that space.
              </p>
            </div>
            <Link
              to="/categories"
              data-testid="see-all-categories-link"
              className="text-sm text-[#B31E1E] font-semibold hover:underline underline-offset-4 inline-flex items-center gap-1"
            >
              See all categories <ArrowRight size={14} />
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="popular-categories-grid">
            {[
              "Home Services",
              "Food & Drink",
              "Beauty & Spas",
              "Auto Services",
              "Pets",
              "Fitness",
              "Health & Medical",
              "Real Estate",
              "Landscaping & Outdoor",
              "Cleaning",
              "Nightlife",
              "Hotels & Travel",
            ].map((group) => {
              const img = CATEGORY_IMAGES[group] || FALLBACK_CATEGORY_IMAGE;
              const count = TAXONOMY.find((t) => t.group === group)?.items?.length || 0;
              return (
                <Link
                  key={group}
                  to={`/directory?category=${encodeURIComponent(group)}`}
                  data-testid={`popular-cat-${group.replace(/\s+/g, "-").toLowerCase()}`}
                  className="group relative aspect-[4/5] rounded-2xl overflow-hidden bg-[#FAF5F2] transition-transform duration-300 hover:-translate-y-1"
                >
                  <img
                    src={img}
                    alt={group}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Dark gradient overlay for legibility */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  {/* Corner subcategory count pill */}
                  <div className="absolute top-3 right-3 rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-mono font-semibold text-[#1B1614] backdrop-blur-sm">
                    {count}+ services
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <div className="text-white font-black text-[18px] leading-tight tracking-tight drop-shadow-lg">
                      {group}
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-white/85 opacity-0 group-hover:opacity-100 transition-opacity">
                      Browse <ArrowRight size={11} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured artists (hidden when there are no approved artists yet) */}
      <FeaturedArtists />

      {/* FEATURES */}
      <section id="features" className="bg-[#FAF5F2] border-b border-[#EAE3DE]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Pill testId="features-eyebrow">Platform features</Pill>
          <h2 className="mt-4 text-4xl md:text-5xl font-black tracking-tight text-[#1B1614] max-w-2xl leading-[1.05]">
            Built for real transactions, not vanity metrics.
          </h2>
          <p className="mt-5 max-w-2xl text-[17px] leading-relaxed text-[#5C4E48]">
            One-directional star ratings are easily gamed. Servrevu's two-sided system creates real accountability — so businesses can trust their customers and customers can trust businesses.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard
              Icon={ArrowLeftRight}
              badge="Core feature"
              title="Mutual accountability"
              body="Businesses rate customers alongside customer reviews. Both parties build reputation — no more one-directional ratings that only tell half the story."
              image="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600"
              testId="feat-mutual"
            />
            <FeatureCard
              Icon={ShieldCheck}
              badge="Integrity"
              title="Verified transactions"
              body="Reviews are tied to real service interactions across home services, food and drink, auto, beauty, pets, and real estate — not anonymous noise."
              image="https://images.pexels.com/photos/6863515/pexels-photo-6863515.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600"
              testId="feat-verified"
            />
            <FeatureCard
              Icon={Lock}
              badge="Privacy"
              title="Privacy by design"
              body="Customer profiles show first initial plus last name with location only. Businesses get accountability without invading customer privacy."
              image="https://images.pexels.com/photos/60504/security-protection-anti-virus-software-60504.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600"
              testId="feat-privacy"
            />
            <FeatureCard
              Icon={Building2}
              badge="Local"
              title="For local businesses"
              body="From independent plumbers to boutique salons, Servrevu helps small businesses distinguish reliable customers from problematic ones."
              image="https://images.pexels.com/photos/264636/pexels-photo-264636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600"
              testId="feat-local"
            />
            <FeatureCard
              Icon={Users}
              badge="Growth"
              title="Loyalty rewards"
              body="Business Pro subscribers can run loyalty programs that reward opted-in customers — turning one-time visitors into regulars."
              image="https://images.pexels.com/photos/851555/pexels-photo-851555.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600"
              testId="feat-loyalty"
            />
            <FeatureCard
              Icon={TrendingUp}
              badge="Pro"
              title="Email promotions"
              body="Business Pro unlocks email promotions to opted-in local customers. Reach the right audience without advertising noise."
              image="https://images.pexels.com/photos/1591062/pexels-photo-1591062.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600"
              testId="feat-email"
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="bg-white border-b border-[#EAE3DE]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10 items-start">
            <div className="md:col-span-3">
              <Pill>How it works</Pill>
              <h2 className="mt-4 text-4xl md:text-5xl font-black tracking-tight text-[#1B1614] leading-[1.05]">
                Accountability works in both directions.
              </h2>
            </div>
            <p className="md:col-span-2 text-[17px] leading-relaxed text-[#5C4E48] md:pt-14">
              Four steps from unknown to trusted — on both sides of every transaction.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            <Step
              n="01"
              title="Customer finds a business"
              body="Browse verified businesses across home services, food and drink, auto, beauty, pets, and real estate. See both customer ratings AND business ratings for each profile."
            />
            <Step
              n="02"
              title="Service happens"
              body="After the transaction, both parties can leave a review. Customers rate the business; businesses rate the customer. Real names, real accountability."
            />
            <Step
              n="03"
              title="Profiles build over time"
              body="Each transaction adds to your reputation history. Businesses see customer reliability; customers see business quality. Both sides benefit from transparency."
            />
            <Step
              n="04"
              title="Better decisions, locally"
              body="Local businesses can reward loyal, reliable customers with Business Pro tools. Customers get a clearer picture of who they&apos;re hiring. Trust is built from both sides."
            />
          </div>

          {/* Mediation trust guarantee */}
          <div
            className="mt-12 relative overflow-hidden rounded-2xl border border-[#F0C9C4] bg-gradient-to-br from-[#FDF3F1] via-white to-white p-6 md:p-8"
            data-testid="how-mediation-callout"
          >
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-xl bg-[#B31E1E] flex items-center justify-center shrink-0">
                <ShieldCheck className="text-white" size={22} />
              </div>
              <div className="flex-1">
                <Pill tone="red">Evidence-backed mediation</Pill>
                <h3 className="mt-3 text-2xl md:text-3xl font-black tracking-tight text-[#1B1614] leading-tight">
                  No rating stands unchallenged.
                </h3>
                <p className="mt-3 text-[15px] md:text-[16px] leading-relaxed text-[#3E2A26] max-w-3xl">
                  When a review is contested, either party may submit supporting documentation — photographs, short video clips, audio recordings, PDFs, or screenshots of text messages — and request formal mediation. Once a dispute is filed, our team reaches out to the other party with the submitted evidence and invites them to provide their account of what happened. Both sides are given a fair opportunity to respond. Should the other party decline to respond or fail to substantiate their position with documentation within the review window, the review will be removed. Every verdict is grounded in the evidence provided by both parties, so the ratings you see on Servrevu accurately reflect real interactions.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Pill tone="muted">Photos &amp; video</Pill>
                  <Pill tone="muted">Audio recordings</Pill>
                  <Pill tone="muted">Text-message screenshots</Pill>
                  <Pill tone="muted">Verdict visible to both sides</Pill>
                </div>
              </div>
            </div>

            {/* Live animation showing the mediation lifecycle end-to-end */}
            <div className="mt-8">
              <ReviewMediationAnimation />
            </div>
          </div>
        </div>
      </section>

      {/* Community mosaic — visual proof point right before pricing.
          Uses curated category images to bring the page alive with real
          scenes of local commerce. */}
      <section className="bg-[#1B1614] text-white" data-testid="community-mosaic-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
                Built on real local commerce
              </div>
              <h2 className="mt-4 text-4xl md:text-5xl font-black tracking-tight leading-[1.05]">
                From your barbershop
                <span className="block text-[#F0C9C4]">to your plumber.</span>
              </h2>
              <p className="mt-5 text-[16px] text-white/70 max-w-md leading-relaxed">
                Every service on Servrevu is run by someone in your neighborhood —
                and every review is from a customer they actually served. No paid placements. No bots.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/directory">
                  <Button
                    className="rounded-lg bg-white text-[#1B1614] hover:bg-[#F5EEE8] h-11 px-5 font-semibold shadow-lg transition-transform hover:scale-[1.03]"
                    data-testid="mosaic-cta-directory"
                  >
                    Browse directory <ArrowRight size={14} className="ml-1.5" />
                  </Button>
                </Link>
                <Link to="/marketplace">
                  <Button
                    variant="outline"
                    className="rounded-lg border-white/25 bg-white/5 text-white hover:bg-white/10 h-11 px-5 font-semibold backdrop-blur transition-transform hover:scale-[1.03]"
                    data-testid="mosaic-cta-marketplace"
                  >
                    Shop marketplace
                  </Button>
                </Link>
              </div>
            </div>

            {/* Photo mosaic — 6 tiles in a bento layout */}
            <div className="grid grid-cols-6 grid-rows-6 gap-2 aspect-square lg:aspect-[4/3]" data-testid="community-mosaic-grid">
              {[
                { key: "Food & Drink",       span: "col-span-3 row-span-3" },
                { key: "Beauty & Spas",      span: "col-span-3 row-span-3" },
                { key: "Home Services",      span: "col-span-2 row-span-3" },
                { key: "Pets",               span: "col-span-2 row-span-3" },
                { key: "Fitness",            span: "col-span-2 row-span-3" },
              ].map(({ key, span }) => {
                const img = CATEGORY_IMAGES[key] || FALLBACK_CATEGORY_IMAGE;
                return (
                  <Link
                    key={key}
                    to={`/directory?category=${encodeURIComponent(key)}`}
                    className={`${span} relative rounded-xl overflow-hidden group transition-transform duration-300 hover:-translate-y-0.5`}
                    data-testid={`mosaic-tile-${key.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <img
                      src={img}
                      alt={key}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-3">
                      <div className="text-white font-bold text-[13px] leading-tight drop-shadow-lg">{key}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-[#FAF5F2] border-b border-[#EAE3DE]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24 text-center">
          <Pill>Pricing</Pill>
          <h2 className="mt-4 text-4xl md:text-5xl font-black tracking-tight text-[#1B1614]">
            Simple, honest pricing.
          </h2>
          <p className="mt-4 text-[17px] text-[#5C4E48]">Start free. Grow when you need to.</p>

          <div className="mt-12 max-w-6xl mx-auto text-left space-y-4" data-testid="pricing-rows">
            {[
              {
                id: "customer-free",
                pillTone: "muted",
                pillText: "For customers",
                name: "Customer",
                blurb: "Find trustworthy local businesses and get rewarded for being a great customer.",
                price: "$0",
                unit: "/forever",
                perks: [
                  "Free customer profile — first initial + last name only",
                  "Browse the full business directory & reviews",
                  "Post ratings and written reviews about businesses",
                  "Receive exclusive promotions from opted-in businesses",
                  "Earn loyalty rewards from your favourite shops",
                  "Join Neighborhood groups & sell in the Marketplace",
                ],
                cta: { to: "/register", label: "Create customer account", sub: "Free forever", tone: "charcoal" },
              },
              {
                id: "influencer",
                pillTone: "red",
                pillText: "For creators",
                name: "Influencer",
                blurb: "Bands, actors, artists, comedians, and anyone with a following. Get a verified badge and the same customer tools businesses use.",
                price: "$9.95",
                unit: "/month",
                perks: [
                  "Verified Influencer badge on your profile",
                  "Access the full customer directory",
                  "Post reviews about the customers you interact with",
                  "Eligible for the $35/mo Marketing add-on",
                  "Perfect for bands, actors, artists, comedians",
                  "Cancel anytime — keep your badge for the paid period",
                ],
                cta: { to: "/influencer/subscribe", label: "Become an Influencer", sub: "$9.95 / month", tone: "purple" },
              },
              {
                id: "business-free",
                pillTone: "muted",
                pillText: "For businesses",
                name: "Business",
                blurb: "List your business for free and start collecting real, verified reviews.",
                price: "$0",
                unit: "/forever",
                perks: [
                  "Free public business listing in the directory",
                  "Collect customer reviews & star ratings",
                  "Basic profile: hours, categories, contact, photos",
                  "Respond publicly to customer reviews",
                  "Show up in category & city search results",
                  "Verified business badge after claim",
                ],
                cta: { to: "/list-your-business", label: "List your business", sub: "Free · no card required", tone: "teal" },
              },
              {
                id: "pro",
                pillTone: "muted",
                pillText: "For businesses",
                name: "Business Pro",
                blurb: "Everything in Business, plus full customer intelligence & marketing tools.",
                price: "$9.95",
                unit: "/month",
                highlight: true,
                perks: [
                  "Everything in Business (Free)",
                  "Full customer ledger — read every review about any customer",
                  "Post your own reviews about the customers you serve",
                  "Email promotions to opted-in local customers",
                  "Loyalty rewards & birthday promo tools",
                  "Priority support and dispute mediation",
                ],
                cta: { to: "/subscription", label: "Upgrade to Pro", sub: "$9.95 / month", tone: "crimson" },
              },
            ].map((tier) => (
              <div
                key={tier.id}
                data-testid={`pricing-${tier.id}-card`}
                className={`relative rounded-2xl border p-6 md:p-8 shadow-[0_4px_24px_-8px_rgba(179,30,30,0.08)] bg-white overflow-hidden transition-all ${
                  tier.highlight ? "border-[#B31E1E]/30 shadow-[0_10px_40px_-15px_rgba(179,30,30,0.35)]" : "border-[#EAE3DE]"
                }`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                  {/* Left — tier identity */}
                  <div className="lg:col-span-3">
                    <div className="inline-flex self-start"><Pill tone={tier.pillTone}>{tier.pillText}</Pill></div>
                    <div className="mt-3 text-2xl font-black text-[#1B1614]">{tier.name}</div>
                    <p className="mt-1.5 text-[13px] text-[#8B7B73] leading-relaxed">{tier.blurb}</p>
                    <div className="mt-4 flex items-baseline gap-1">
                      <span className="text-4xl font-black tracking-tight text-[#1B1614]">{tier.price}</span>
                      <span className="text-sm text-[#8B7B73]">{tier.unit}</span>
                    </div>
                  </div>

                  {/* Middle — perk grid */}
                  <ul className="lg:col-span-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5 text-[13.5px] text-[#3E2A26]">
                    {tier.perks.map((l) => (
                      <li key={l} className="flex items-start gap-2">
                        <Check size={14} className="text-[#B31E1E] shrink-0 mt-0.5" /> <span>{l}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Right — CTA */}
                  <div className="lg:col-span-3 lg:pl-3 lg:border-l lg:border-[#EAE3DE] flex flex-col justify-center">
                    <Link to={tier.cta.to} className="block">
                      <Button
                        data-testid={`pricing-${tier.id}-cta`}
                        className={`w-full h-[64px] rounded-lg font-semibold whitespace-nowrap leading-tight py-2 px-3 flex flex-col items-center justify-center gap-0.5 transition-all duration-200 hover:scale-[1.04] text-white ${
                          tier.cta.tone === "charcoal" ? "bg-[#1B1614] hover:bg-black shadow-md hover:shadow-[0_10px_28px_-6px_rgba(27,22,20,0.55)]" :
                          tier.cta.tone === "teal"     ? "bg-[#0F766E] hover:bg-[#0B5C55] shadow-md hover:shadow-[0_10px_28px_-6px_rgba(15,118,110,0.6)]" :
                          tier.cta.tone === "purple"   ? "bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] hover:from-[#6D28D9] hover:to-[#4338CA] shadow-[0_6px_20px_-8px_rgba(124,58,237,0.6)] hover:shadow-[0_14px_36px_-8px_rgba(124,58,237,0.85)]" :
                                                       "bg-[#B31E1E] hover:bg-[#8F1717] shadow-[0_6px_20px_-8px_rgba(179,30,30,0.6)] hover:shadow-[0_14px_36px_-8px_rgba(179,30,30,0.85)]"
                        }`}
                      >
                        <span className="text-[14px]">{tier.cta.label}</span>
                        <span className="text-[11px] font-normal opacity-90">{tier.cta.sub}</span>
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-sm text-[#8B7B73]">All prices in USD. No long-term contracts. Cancel anytime.</p>

          {/* Marketing add-on */}
          <div className="mt-16 max-w-5xl mx-auto text-left" data-testid="marketing-addon-section">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
              <div>
                <Pill tone="red" testId="marketing-addon-eyebrow">Optional add-on · Business Pro</Pill>
                <h3 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-[#1B1614]">
                  Marketing upgrade
                </h3>
                <p className="mt-2 text-[16px] text-[#5C4E48] max-w-2xl">
                  Turn Servrevu into a full-service local marketing channel — reach opted-in customers with campaigns, coupons, and behaviour-triggered promotions.
                </p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black tracking-tight text-[#1B1614]">
                  +$35<span className="text-lg font-semibold text-[#8B7B73]">/mo</span>
                </div>
                <div className="text-xs text-[#8B7B73] mt-1">Requires active Business Pro</div>
              </div>
            </div>

            <div className="rounded-2xl border border-[#EAE3DE] bg-white p-8 shadow-[0_4px_24px_-8px_rgba(179,30,30,0.10)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
                {[
                  { Icon: Target,     title: "Advanced audience targeting",  body: "Segment opted-in customers by category, city, ZIP code, favourites and last visit." },
                  { Icon: Mail,       title: "Unlimited broadcast emails",    body: "Send campaigns with photos, buttons, coupon codes, and rich-text via the built-in email builder." },
                  { Icon: Gift,       title: "Coupons & limited-time offers", body: "Attach unique redeemable coupon codes with expiration dates and per-customer limits." },
                  { Icon: Sparkles,   title: "Birthday & anniversary autopilot", body: "Auto-send personalised birthday and re-engagement promos to loyal customers." },
                  { Icon: Megaphone,  title: "Referral program tools",         body: "Reward customers who bring their friends with tracked referral links and bonus offers." },
                  { Icon: BarChart3,  title: "Marketing analytics dashboard",  body: "Track opens, clicks, redemptions, and campaign ROI in one report card." },
                ].map((f) => (
                  <div key={f.title} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#F9E4E1] flex items-center justify-center shrink-0">
                      <f.Icon size={16} className="text-[#B31E1E]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#1B1614]">{f.title}</div>
                      <div className="text-sm text-[#5C4E48] mt-0.5 leading-relaxed">{f.body}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-[#EAE3DE] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="text-sm text-[#5C4E48]">
                  Total with add-on: <span className="font-semibold text-[#1B1614]">$34.95/mo</span> · Cancel the add-on anytime, keep Pro.
                </div>
                <Link to="/subscription">
                  <Button
                    data-testid="marketing-addon-cta"
                    className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white h-11 px-5 font-semibold"
                  >
                    Add Marketing to Pro <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DARK CTA */}
      <section className="bg-[#FDFBF9]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20">
          <div className="relative overflow-hidden rounded-3xl bg-[#141210] text-white px-8 md:px-14 py-16 md:py-20 text-center">
            <Quote
              aria-hidden
              className="absolute top-8 right-8 text-white/5"
              size={140}
            />
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-[1.1] max-w-2xl mx-auto">
              Ready to build reputation on both sides?
            </h2>
            <p className="mt-6 max-w-xl mx-auto text-[16px] leading-relaxed text-white/70">
              Join Servrevu today and start building mutual accountability in your community. Free to list. No credit card required.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 justify-center items-center">
              <Link to="/list-your-business">
                <Button
                  data-testid="cta-list-business"
                  className="rounded-lg bg-[#F5EEE8] text-[#141210] hover:bg-white h-11 px-5 font-semibold"
                >
                  List your business <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <a href="#features" className="text-white/85 hover:text-white text-[15px] font-medium underline-offset-4 hover:underline">
                Learn more
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
